/*
 * Copyright (c) Nexign, JSC, 2019-2022.
 */
/**
 * List component for WidgetOrder
 *
 * @author Fedorov Maxim
 * @author Yakov Gushchin
 * @author Aleksandr Perkhunov
 * @author Anton Panasenko
 * @version 1.19.0
 * @since 1.0.1
 */

import { FC, Fragment } from 'react';

import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';

import { FlexStyled } from '../../../../../../components/common/Block/Flexbox';
import { ImgStyled, LinkStyled } from '../../../../../../components/common/Block/Img';
import LinkEllipseBlock from '../../../../../../components/common/LinkBlock/LinkEllipseBlock';
import ArrowTooltip from '../../../../../../components/common/Tooltip/Tooltip';
import GTMOperations from '../../../../../../modules/analytics/GoogleTagManager/GTMOperations';
import { EGTMWidgetOrderInquiriesTab } from '../../../../../../modules/analytics/GoogleTagManager/GTMOperations/GTMWidgetOrderInquiriesOperations';
import { IGTMPushAnalyticPayload } from '../../../../../../modules/analytics/GoogleTagManager/types/GTMActionTypes';
import { guardComponentType } from '../../../../../../modules/role/reducer/guardReducer';
import { makeSelectGuardComponents } from '../../../../../../modules/role/selectors/guardSelector';
import { IRootReducer } from '../../../../../../reducers/rootReducer';
import { openExternalRoute, openRoute } from '../../../../../../routes/constants/externalRoutes';
import { HistoryOrderActionFilesIcon } from '../../../../../../types/orderTypes';
import { formatDate } from '../../../../../../utils/formatText';
import { getOrderStatusIcon } from '../../../../../PageHistoryOrder/utils/getOrderStatusIcon';
import {
    getPageOrderIdFileUrl,
    getPageOrderIdUrl,
    isPageOrderIdExternalUrl,
} from '../../../../../PageOrderId/utils/getPageOrderIdUrl';
import { WidgetItemLeftContainerStyled, WidgetListItem } from '../../styled/WidgetStyled';
import { WidgetOrderInquiriesListItemDateStyled } from '../styled/widgetOrderInquiriesStyled';
import { IWidgetOrders } from '../widgetOrderInquiriesTypes';

export interface IWidgetOrdersList {
    orders: IWidgetOrders[];
    pushGtmAnalytic: (payload: IGTMPushAnalyticPayload) => void;
}

export const WidgetOrdersList: FC<IWidgetOrdersList & IConnectedProps> = props => {
    const { orders, componentsFeature } = props;

    const onClick = (item: string, name: string): void => {
        props.pushGtmAnalytic({
            operation: GTMOperations.widgetOrderInquiries
                .clickLinkOrderOrInquiryWidgetOrderInquiries(name, EGTMWidgetOrderInquiriesTab.orders),
            withAccountType: true,
            withSusrId: true,
            withUserRole: true,
        });
        const url = getPageOrderIdUrl(componentsFeature, item);
        isPageOrderIdExternalUrl(componentsFeature) ? openExternalRoute(url) : openRoute(url);
    };

    const onDownloadLinkClick = (name: string): void => {
        props.pushGtmAnalytic({
            operation: GTMOperations.widgetOrderInquiries.clickLinkOrderOrInquiryWidgetDownloadFile(name),
            withAccountType: true,
            withSusrId: true,
            withUserRole: true,
        });
    };

    return (
        <Fragment>
            {
                orders.map((order: IWidgetOrders) =>
                    <WidgetListItem key={order.id}>
                        <WidgetItemLeftContainerStyled>
                            <LinkEllipseBlock
                                className="widget-order-inquiries__item-link"
                                dataName="redirectToOrder"
                                isExternalUrl={isPageOrderIdExternalUrl(componentsFeature)}
                                title={order.name}
                                url={getPageOrderIdUrl(componentsFeature, order.id.toString())}
                                onClick={() => onClick(order.id.toString(), order.name)}
                            >
                                {order.name}
                            </LinkEllipseBlock>
                            <WidgetOrderInquiriesListItemDateStyled>
                                {formatDate(order.date)}
                            </WidgetOrderInquiriesListItemDateStyled>
                        </WidgetItemLeftContainerStyled>
                        <FlexStyled
                            alignItems="center"
                            justifyContent={order.files.length ? 'space-between' : 'flex-end'}
                        >
                            {
                                order.files.map(file =>
                                    <LinkStyled
                                        className="medium"
                                        data-link="downloadFile"
                                        href={getPageOrderIdFileUrl(componentsFeature, order.id, file.id?.toString())}
                                        key={order.id + order.name}
                                        onClick={() => onDownloadLinkClick(order.name)}
                                    >
                                        <ImgStyled
                                            className="medium pointer"
                                            key={file.fileType + file.name}
                                            src={HistoryOrderActionFilesIcon[file.fileType]}
                                        />
                                    </LinkStyled>,
                                )
                            }
                            <ArrowTooltip
                                dataName="toolTipStatus"
                                title={order.status.def}
                            >
                                <ImgStyled
                                    className="small"
                                    src={getOrderStatusIcon(order.status.id)}
                                    width="20px"
                                />
                            </ArrowTooltip>
                        </FlexStyled>
                    </WidgetListItem>,
                )
            }
        </Fragment>
    );
};

export interface IConnectedProps {
    componentsFeature: guardComponentType;
}

const mapStateToProps = createStructuredSelector<IRootReducer, IConnectedProps>({
    componentsFeature: makeSelectGuardComponents(),
});

export default connect(mapStateToProps)(WidgetOrdersList);
